import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { AnalysisCharacter, AnalysisProp, ImageModel } from "../../../types";
import { tosThumbnail } from "../../../utils/tosImage";
import {
  importAnalysisRefUrl,
  addAnalysisElement,
  uploadCharacterVoice,
  isApiError,
} from "../../../api";
import { useToast } from "../../../components/Toast";
import type { ElementType } from "../types";
import { activeTaskKey, ActiveImageTask, SlotFailure } from "../utils/activeImageTasks";
import { BaseImageBlock } from "./BaseImageBlock";
import { ElementCard } from "./ElementCard";
import { EpisodeBadges } from "./EpisodeBadges";
import { Lightbox } from "./Lightbox";
import { Tooltip } from "./Tooltip";
import { VoiceChip } from "./VoiceChip";
import { getAudioDuration } from "../utils/audio";
import CharacterStyleWorkspace from "./CharacterStyleWorkspace";
import AssetGenInfoPanel from "./AssetGenInfoPanel";
import AssetCommentButton from "./AssetCommentButton";
import { useRegistry } from "../../../utils/useRegistry";
import { useLocation, useNavigate } from "react-router-dom";
import { useOpenElementTarget } from "../hooks/useOpenElementTarget";
import EpisodeMultiSelect from "../../../components/EpisodeMultiSelect";
import { canConfirmAddState } from "../utils/addStateForm";
import type { BaseMetadataField, ResolvedBaseMetadata } from "../utils/baseMetadata";

interface CharacterGroupProps {
  defaultCollapsed?: boolean;
  projectId: string;
  name: string;
  states: AnalysisCharacter[];
  basicState?: AnalysisCharacter;
  baseMetadata: ResolvedBaseMetadata;
  analysisAuthorityRevision: number;
  voiceUrl?: string;
  onUpdate: (
    type: ElementType,
    name: string,
    imagePrompt: string,
    imageUrls?: string[],
    refImageUrls?: string[],
    selectedImageUrl?: string,
    promptIncludesPrefix?: boolean,
  ) => void;
  onAddState: (name: string, state: string, desc: string, episodes: number[]) => void;
  activeTaskMap: Map<string, ActiveImageTask[]>;
  failedTaskMap?: Map<string, SlotFailure[]>;
  baseActiveTaskMap?: Map<string, ActiveImageTask[]>;
  baseFailedTaskMap?: Map<string, SlotFailure[]>;
  onTaskResolved?: (taskId: string) => void;
  fixedPrompt: string;
  projectRefImages?: string[];
  episodeMap?: Map<number, string>;
  charStatePromptPrefix?: string;
  onBaseGenerated?: () => void | boolean | Promise<void | boolean>;
  onGenerateComplete?: () => void;
  onNavigateToProp?: (propName: string) => void;
  allProps?: AnalysisProp[];
  onSearchSelect?: (
    elementType: string,
    elementName: string,
    state: string,
    currentRefCount: number,
  ) => void;
  getSearchImageCount?: (
    elementType: string,
    elementName: string,
    state: string,
  ) => number;
  onSearchSelectAllStates?: (
    elementType: string,
    elementName: string,
    currentRefCount: number,
  ) => void;
  getSearchImageCountAllStates?: (
    elementType: string,
    elementName: string,
  ) => number;
  getSearchTaskProgress?: (
    elementType: string,
    elementName: string,
    state: string,
  ) => { completed: number; total: number };
  getSearchTaskProgressAllStates?: (
    elementType: string,
    elementName: string,
  ) => { completed: number; total: number };
  isDisabled?: boolean;
  onToggleDisable?: (name: string, isCurrentlyDisabled: boolean) => void;
  disableLoading?: boolean;
  onRename?: (name: string) => void;
  onComment?: (name: string) => void;
  pendingCommentCount?: number;
  reviewStatusMap?: Map<string, string>;
  onReviewInvalidated?: () => void;
  availableEpisodes?: readonly { episodeIndex: number; title?: string }[];
  onEpisodesUpdate?: (name: string, state: string, episodes: number[]) => void;
  characterImageWorkflow?: "legacy" | "casting_styling";
  imagePlatform?: string;
  imageModel?: string;
  imageModels?: ImageModel[];
}

export const CharacterGroup: React.FC<CharacterGroupProps> = ({
  projectId,
  name,
  states,
  basicState,
  baseMetadata,
  analysisAuthorityRevision,
  voiceUrl,
  onUpdate,
  onAddState,
  activeTaskMap,
  failedTaskMap,
  baseActiveTaskMap,
  baseFailedTaskMap,
  onTaskResolved,
  defaultCollapsed = false,
  fixedPrompt,
  projectRefImages = [],
  episodeMap,
  charStatePromptPrefix,
  onBaseGenerated,
  onGenerateComplete,
  onNavigateToProp,
  allProps = [],
  onSearchSelect,
  getSearchImageCount,
  onSearchSelectAllStates,
  getSearchImageCountAllStates,
  getSearchTaskProgress,
  getSearchTaskProgressAllStates,
  isDisabled,
  onToggleDisable,
  disableLoading,
  onRename,
  onComment,
  pendingCommentCount,
  reviewStatusMap,
  onReviewInvalidated,
  availableEpisodes,
  onEpisodesUpdate,
  characterImageWorkflow = "legacy",
  imagePlatform,
  imageModel,
  imageModels = [],
}) => {
  const { t } = useTranslation("projectDetail");
  const toast = useToast();
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const voiceInputRef = useRef<HTMLInputElement>(null);
  const [styleWorkspaceOpen, setStyleWorkspaceOpen] = useState(false);

  const handleVoiceUpload = async (file: File) => {
    // Keep voice uploads within the backend duration limit.
    try {
      const duration = await getAudioDuration(file);
      if (duration > 15) {
        toast.error(
          t("character.voiceDurationExceeded", {
            duration: duration.toFixed(1),
          }),
        );
        return;
      }
    } catch {
      toast.error(t("character.voiceReadFailed"));
      return;
    }
    setUploadingVoice(true);
    try {
      await uploadCharacterVoice(projectId, name, file);
      toast.success(t("character.voiceUploadSuccess"));
      onBaseGenerated?.();
    } catch (e: any) {
      toast.error(e.message || t("character.voiceUploadFailed"));
    } finally {
      setUploadingVoice(false);
    }
  };
  const [collapsed] = useState(defaultCollapsed);
  const stateKey = (state?: string) => state || "";
  const baseStateKey = "__base__";
  const [serverBaseConflictFields, setServerBaseConflictFields] = useState<BaseMetadataField[]>([]);
  useEffect(() => {
    setServerBaseConflictFields([]);
  }, [analysisAuthorityRevision]);
  const effectiveBaseConflictFields = Array.from(new Set<BaseMetadataField>([
    ...baseMetadata.conflictFields,
    ...serverBaseConflictFields,
  ]));
  const selectedBaseImageUrl = effectiveBaseConflictFields.includes(
    "selectedBaseImageUrl",
  )
    ? ""
    : baseMetadata.selectedBaseImageUrl;
  const hasBaseMetadataConflict = effectiveBaseConflictFields.length > 0;
  const [activeStateKey, setActiveStateKey] = useState(() => {
    const selectedIdx = states.findIndex((s) => !!s.selectedImageUrl);
    if (selectedIdx >= 0) return stateKey(states[selectedIdx]?.state);
    if (selectedBaseImageUrl) return baseStateKey;
    return stateKey(states[0]?.state);
  });
  const [currentBaseImageUrl, setCurrentBaseImageUrl] = useState<
    string | undefined
  >(selectedBaseImageUrl || undefined);
  useEffect(() => {
    setCurrentBaseImageUrl(selectedBaseImageUrl || undefined);
  }, [selectedBaseImageUrl]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [addingState, setAddingState] = useState(false);
  const [newStateName, setNewStateName] = useState("");
  const [newStateDesc, setNewStateDesc] = useState("");
  const [newStateEpisodes, setNewStateEpisodes] = useState<number[]>([]);
  const [addingLoading, setAddingLoading] = useState(false);
  const [importingProp, setImportingProp] = useState<string | null>(null);
  const [propStatePicker, setPropStatePicker] = useState<{
    propName: string;
    options: typeof allProps;
  } | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const baseKey = activeTaskKey("character", name);
  const inFlightBase = baseActiveTaskMap?.get(baseKey) ?? [];
  const baseFailures = baseFailedTaskMap?.get(baseKey) ?? [];
  // 仅在制作弹窗打开时拉取 registry（资深资产分析才有；拿不到则不显示信息栏）
  const { data: registry } = useRegistry(
    showHistoryModal ? projectId : undefined,
  );
  // 仅当 registry 确有数据时才显示信息栏（非资深项目可能返回空对象，需回退原弹窗）
  const hasGenInfo =
    !!registry &&
    (registry.episodes.length > 0 ||
      registry.characters.length > 0 ||
      registry.sceneProps.length > 0);
  // 从分镜页「更改」深链进入时（?openElement=character:名&makeState=状态），自动打开该状态的制作弹窗一次
  const openElement = useOpenElementTarget();
  const location = useLocation();
  const navigate = useNavigate();
  const makeStateHandledRef = useRef(false);
  useEffect(() => {
    if (makeStateHandledRef.current) return;
    if (
      !openElement ||
      openElement.type !== "character" ||
      openElement.name !== name
    )
      return;
    const makeState = new URLSearchParams(location.search).get("makeState");
    if (makeState === null) return;
    makeStateHandledRef.current = true;
    setActiveStateKey(stateKey(makeState));
    setShowHistoryModal(true);
    const sp = new URLSearchParams(location.search);
    sp.delete("makeState");
    navigate(
      {
        pathname: location.pathname,
        search: sp.toString() ? `?${sp.toString()}` : "",
      },
      { replace: true },
    );
  }, [openElement, location.pathname, location.search, name, navigate]);
  const addStateHandledRef = useRef(false);
  useEffect(() => {
    if (addStateHandledRef.current) return;
    if (
      !openElement ||
      openElement.type !== "character" ||
      openElement.name !== name
    )
      return;
    const addState = new URLSearchParams(location.search).get("addState");
    if (addState === null) return;
    addStateHandledRef.current = true;
    if (!hasBaseMetadataConflict) setAddingState(true);
    const sp = new URLSearchParams(location.search);
    sp.delete("addState");
    navigate(
      {
        pathname: location.pathname,
        search: sp.toString() ? `?${sp.toString()}` : "",
      },
      { replace: true },
    );
  }, [openElement, location.pathname, location.search, name, navigate, hasBaseMetadataConflict]);
  const [seenCounts, setSeenCounts] = useState<Record<string, number>>(() => {
    const c: Record<string, number> = {};
    const pfx = `thumb-seen:${projectId}:${name}`;
    const bv = localStorage.getItem(`${pfx}:base`);
    c["base"] =
      bv !== null ? parseInt(bv, 10) : baseMetadata.baseImageUrls.length;
    if (bv === null) localStorage.setItem(`${pfx}:base`, String(c["base"]));
    states.forEach((s) => {
      const k = s.state || "default";
      const sv = localStorage.getItem(`${pfx}:${k}`);
      c[k] = sv !== null ? parseInt(sv, 10) : s.imageUrls?.length || 0;
      if (sv === null) localStorage.setItem(`${pfx}:${k}`, String(c[k]));
    });
    return c;
  });
  const markThumbSeen = (key: string, count: number) => {
    localStorage.setItem(
      `thumb-seen:${projectId}:${name}:${key}`,
      String(count),
    );
    setSeenCounts((prev) => ({ ...prev, [key]: count }));
    window.dispatchEvent(new Event("thumb-seen-update"));
  };
  const activeIdx =
    activeStateKey === baseStateKey
      ? -1
      : Math.max(
          0,
          states.findIndex((s) => stateKey(s.state) === activeStateKey),
        );
  const safeIdx = activeIdx < 0 ? 0 : Math.min(activeIdx, states.length - 1);
  const c = states[safeIdx];
  const fixedDescription =
    basicState ||
    states.reduce(
      (best, s) =>
        (s.episodes?.length || 0) >= (best.episodes?.length || 0) ? s : best,
      states[0],
    );
  const activeDescription = fixedDescription;
  const elementName = c.state ? `${c.name}|${c.state}` : c.name;
  const mostEp = states.reduce(
    (best, s) =>
      (s.episodes?.length || 0) >= (best.episodes?.length || 0) ? s : best,
    states[0],
  );

  const activeThumbKey = showHistoryModal
    ? activeIdx === -1
      ? "b:" + baseMetadata.baseImageUrls.length
      : "s:" + activeIdx + ":" + (states[safeIdx]?.imageUrls?.length || 0)
    : "";
  useEffect(() => {
    if (!showHistoryModal) return;
    const pfx = `thumb-seen:${projectId}:${name}`;
    if (activeIdx === -1) {
      const cnt = baseMetadata.baseImageUrls.length;
      localStorage.setItem(`${pfx}:base`, String(cnt));
      setSeenCounts((prev) => ({ ...prev, base: cnt }));
    } else if (safeIdx < states.length) {
      const s = states[safeIdx];
      const k = s.state || "default";
      const cnt = s.imageUrls?.length || 0;
      localStorage.setItem(`${pfx}:${k}`, String(cnt));
      setSeenCounts((prev) => ({ ...prev, [k]: cnt }));
    }
    window.dispatchEvent(new Event("thumb-seen-update"));
  }, [activeThumbKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [previewOrientation, setPreviewOrientation] = useState<
    "square" | "landscape" | "portrait"
  >("square");

  const selectedStateImageUrl = states[safeIdx]?.selectedImageUrl;

  useEffect(() => {
    if (collapsed) return;
    const url =
      activeIdx === -1
        ? currentBaseImageUrl
        : selectedStateImageUrl;
    if (!url) {
      setPreviewOrientation("portrait");
      return;
    }
    const img = new Image();
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      if (ratio > 1.1) setPreviewOrientation("landscape");
      else if (ratio < 0.9) setPreviewOrientation("portrait");
      else setPreviewOrientation("square");
    };
    img.onerror = () => setPreviewOrientation("square");
    img.src = tosThumbnail(url, 64);
  }, [
    activeIdx,
    currentBaseImageUrl,
    selectedStateImageUrl,
    collapsed,
  ]);

  const previewFlex = previewOrientation === "portrait" ? "0 0 15%" : "0 0 35%";
  const previewAspectRatio = previewOrientation === "square" ? "1" : undefined;
  const thumbColumns = previewOrientation === "portrait" ? 8 : 6;

  const doImportPropRef = async (prop: (typeof allProps)[0]) => {
    if (!prop.selectedImageUrl) return;
    const label = prop.state ? `${prop.name}(${prop.state})` : prop.name;
    setImportingProp(prop.name);
    setPropStatePicker(null);
    try {
      const { refImageUrls: updatedUrls } = await importAnalysisRefUrl(
        projectId,
        "character",
        elementName,
        prop.selectedImageUrl,
      );
      onUpdate(
        "character",
        elementName,
        c.imagePrompt || "",
        c.imageUrls,
        updatedUrls,
        c.selectedImageUrl,
        c.promptIncludesPrefix,
      );
      toast.success(t("character.importedPropSuccess", { label }));
    } catch (err: any) {
      toast.error(t("character.importFailed", { error: err.message || err }));
    } finally {
      setImportingProp(null);
    }
  };

  const handleImportPropRef = (propName: string) => {
    const propsWithImage = allProps.filter(
      (p) => p.name === propName && p.selectedImageUrl,
    );
    if (propsWithImage.length === 0) return;
    if (propsWithImage.length === 1) {
      doImportPropRef(propsWithImage[0]);
    } else {
      setPropStatePicker({ propName, options: propsWithImage });
    }
  };


  const handleConfirmAddState = async () => {
    if (hasBaseMetadataConflict) return;
    if (!canConfirmAddState({
      name: newStateName,
      episodeCount: newStateEpisodes.length,
      loading: addingLoading,
    })) return;
    setAddingLoading(true);
    try {
      await addAnalysisElement(
        projectId,
        "character",
        name,
        newStateName.trim(),
        newStateDesc.trim(),
        newStateEpisodes,
      );
      onAddState(name, newStateName.trim(), newStateDesc.trim(), newStateEpisodes);
      setActiveStateKey(newStateName.trim());
      setAddingState(false);
      setNewStateName("");
      setNewStateDesc("");
      setNewStateEpisodes([]);
    } catch (error: unknown) {
      const fields = isApiError(error) && error.status === 409 && error.code === "base_metadata_conflict"
        ? (error.fields ?? []).filter(
          (field): field is BaseMetadataField =>
            field === "baseRefImages" || field === "baseImageUrls" || field === "selectedBaseImageUrl",
        )
        : [];
      if (fields.length > 0) {
        setServerBaseConflictFields(current => Array.from(new Set([...current, ...fields])));
        toast.error(t("baseImage.conflict.addStateBlocked"));
        onBaseGenerated?.();
      } else {
        toast.error(t("addElement.addFailed"));
      }
    } finally {
      setAddingLoading(false);
    }
  };

  const cancelAdd = () => {
    setAddingState(false);
    setNewStateName("");
    setNewStateDesc("");
    setNewStateEpisodes([]);
  };

  const canAddState = !hasBaseMetadataConflict && canConfirmAddState({
    name: newStateName,
    episodeCount: newStateEpisodes.length,
    loading: addingLoading,
  });

  return (
    <div
      id={`elem-character-${name}`}
      style={{
        borderRadius: "8px",
        overflow: "hidden",
        background: "var(--surface)",
        opacity: isDisabled ? 0.5 : 1,
        transition: "opacity 0.2s",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header: name and voice */}
      <div
        style={{
          padding: "0.6rem 0.75rem 0.45rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          background:
            "color-mix(in srgb, var(--theme-character) 12%, var(--surface))",
          borderRadius: "7px 7px 0 0",
        }}
      >
        <p
          style={{
            margin: 0,
            fontWeight: 600,
            fontSize: "0.95rem",
            color: "var(--theme-character)",
            textDecoration: isDisabled ? "line-through" : "none",
          }}
        >
          {name}
        </p>
        {isDisabled && (
          <span
            style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 500 }}
          >
            {t("character.disabled")}
          </span>
        )}
        {onToggleDisable && (
          <button
            onClick={() => onToggleDisable(name, !!isDisabled)}
            disabled={disableLoading}
            style={{
              background: "none",
              border: "1px solid var(--border)",
              cursor: "pointer",
              padding: "1px 8px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              color: isDisabled ? "#22c55e" : "#9ca3af",
              fontWeight: 500,
              lineHeight: 1.6,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = isDisabled
                ? "#22c55e"
                : "#ef4444";
              e.currentTarget.style.color = isDisabled ? "#16a34a" : "#ef4444";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--border)";
              e.currentTarget.style.color = isDisabled ? "#22c55e" : "#9ca3af";
            }}
            title={
              isDisabled
                ? t("character.enableTitle")
                : t("character.disableTitle")
            }
          >
            {isDisabled ? t("character.enable") : t("character.disable")}
          </button>
        )}
        {onRename && !isDisabled && (
          <button
            onClick={() => onRename(name)}
            style={{
              background: "none",
              border: "1px solid var(--border)",
              cursor: "pointer",
              padding: "1px 8px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              color: "#6b7280",
              fontWeight: 500,
              lineHeight: 1.6,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--primary)";
              e.currentTarget.style.color = "var(--primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--border)";
              e.currentTarget.style.color = "#6b7280";
            }}
            title={t("character.renameTitle")}
          >
            {t("character.rename")}
          </button>
        )}
        {onComment && (
          <AssetCommentButton
            projectId={projectId}
            category="character"
            name={name}
            count={pendingCommentCount ?? 0}
            onClick={() => onComment(name)}
            t={t}
          />
        )}
        {/* Voice controls */}
        <VoiceChip
          voiceUrl={voiceUrl}
          uploading={uploadingVoice}
          onUploadClick={() => voiceInputRef.current?.click()}
        />
        <input
          ref={voiceInputRef}
          type="file"
          accept=".wav,.mp3,.m4a,.ogg,.flac"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleVoiceUpload(f);
            e.target.value = "";
          }}
        />
      </div>
      {!isDisabled && characterImageWorkflow === "casting_styling" && (
        <CharacterStyleWorkspace
          open={styleWorkspaceOpen}
          projectId={projectId}
          characterName={name}
          charFixedPrompt={fixedPrompt}
          projectRefImages={projectRefImages}
          platform={imagePlatform}
          model={imageModel}
          baseMetadata={baseMetadata}
          analysisAuthorityRevision={analysisAuthorityRevision}
          onBaseMetadataChanged={onBaseGenerated}
          onClose={() => {
            setStyleWorkspaceOpen(false);
            onBaseGenerated?.();
          }}
        />
      )}
      {!isDisabled && characterImageWorkflow !== "casting_styling" && (
        <div style={{ display: "none" }}>
          {(() => {
            const mostEp = states.reduce(
              (best, s) =>
                (s.episodes?.length || 0) >= (best.episodes?.length || 0)
                  ? s
                  : best,
              states[0],
            );
            return (
              <BaseImageBlock
                projectId={projectId}
                elementType="character"
                name={name}
                fixedPrompt={fixedPrompt}
                mostEpisodesPrompt={mostEp?.imagePrompt || ""}
                initBaseRefImages={baseMetadata.baseRefImages}
                initBaseImageUrls={baseMetadata.baseImageUrls}
                initSelectedBaseImageUrl={selectedBaseImageUrl || undefined}
                conflictFields={effectiveBaseConflictFields}
                analysisAuthorityRevision={analysisAuthorityRevision}
                onSelectBaseImage={(url) =>
                  setCurrentBaseImageUrl(url || undefined)
                }
                onBaseGenerated={onBaseGenerated}
                activeBaseTasks={inFlightBase}
                projectRefImages={projectRefImages}
                failedBaseTasks={baseFailures}
                onSearchSelect={
                  onSearchSelectAllStates
                    ? (count: number) =>
                        onSearchSelectAllStates("character", name, count)
                    : undefined
                }
                searchImageCount={
                  getSearchImageCountAllStates
                    ? getSearchImageCountAllStates("character", name)
                    : 0
                }
                searchTaskProgress={
                  getSearchTaskProgressAllStates
                    ? getSearchTaskProgressAllStates("character", name)
                    : undefined
                }
              />
            );
          })()}
        </div>
      )}

      {!isDisabled && (
        <>
          {/* ── 2-column unified layout ── */}
          <div style={{ display: "flex", gap: "1rem", padding: "0.75rem" }}>
            {/* Left preview */}
            <div
              style={{
                flex: previewFlex,
                display: "flex",
                flexDirection: "column",
                gap: "0.3rem",
                minWidth: 0,
              }}
            >
              {activeIdx !== -1 &&
                states[safeIdx]?.episodes &&
                states[safeIdx].episodes!.length > 0 && (
                  <EpisodeBadges
                    projectId={projectId}
                    episodes={states[safeIdx].episodes}
                    episodeMap={episodeMap}
                  />
                )}
              {/* State label — 显示在大预览图上方，蓝字 + 蓝色圆角矩形 */}
              <div
                style={{
                  alignSelf: "flex-start",
                  background: "rgba(59,130,246,0.1)",
                  color: "#2563eb",
                  border: "1px solid rgba(59,130,246,0.35)",
                  fontSize: "0.82rem",
                  padding: "2px 10px",
                  borderRadius: "6px",
                }}
              >
                {activeIdx === -1
                  ? t("baseImage.title")
                  : c.state || t("character.defaultState")}
              </div>
              <div
                style={{
                  aspectRatio: previewAspectRatio,
                  background: "transparent",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  overflow: "hidden",
                  cursor: (
                    activeIdx === -1 ? currentBaseImageUrl : c.selectedImageUrl
                  )
                    ? "zoom-in"
                    : "default",
                  position: "relative",
                }}
                onClick={() => {
                  const url =
                    activeIdx === -1 ? currentBaseImageUrl : c.selectedImageUrl;
                  if (url) setPreviewUrl(url);
                }}
              >
                {(() => {
                  const previewSrc =
                    activeIdx === -1 ? currentBaseImageUrl : c.selectedImageUrl;
                  if (previewSrc) {
                    const thumbUrl =
                      previewOrientation === "landscape"
                        ? tosThumbnail(previewSrc, 0, 400)
                        : previewOrientation === "square"
                          ? tosThumbnail(previewSrc, 500, 500)
                          : tosThumbnail(previewSrc, 400);
                    return (
                      <img
                        src={thumbUrl}
                        alt={name}
                        style={{ width: "100%", objectFit: "contain" }}
                      />
                    );
                  }
                  return (
                    <div
                      className="asset-placeholder"
                      style={{
                        width: "100%",
                        aspectRatio: "9/16",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="/VRLOGO.svg"
                        alt=""
                        className="asset-placeholder-logo"
                        style={{
                          width: "64px",
                          height: "64px",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Right */}
            <div
              style={{
                flex: "1 1 0%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: "0.6rem",
              }}
            >
              {/* Row: Base Image (left) + Description (right) — same grid as state thumbnails for consistent cell size */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${thumbColumns}, 1fr)`,
                  gap: "0.4rem 1rem",
                  marginBottom: "0.4rem",
                }}
              >
                {/* Base Image — occupies 1 grid cell */}
                <div>
                  <div
                    style={{
                      fontSize: "0.93rem",
                      fontWeight: 600,
                      color: "var(--theme-character)",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.35rem",
                      marginBottom: "0.3rem",
                    }}
                  >
                    <span
                      style={{
                        width: "3px",
                        height: "1rem",
                        background: "var(--theme-character)",
                        borderRadius: "2px",
                        flexShrink: 0,
                      }}
                    />
                    {t("sectionTitle.baseImage")}
                  </div>
                  <div
                    onClick={() => setActiveStateKey(baseStateKey)}
                    style={{
                      aspectRatio: "1",
                      borderRadius: "6px",
                      overflow: "hidden",
                      cursor: "pointer",
                      background: currentBaseImageUrl
                        ? "transparent"
                        : baseMetadata.baseImageUrls.length > 0
                          ? "rgba(234, 179, 8, 0.12)"
                          : "rgba(156, 163, 175, 0.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      outline:
                        activeIdx === -1
                          ? "3px solid var(--theme-scene)"
                          : "1px solid var(--border)",
                      outlineOffset: activeIdx === -1 ? "1px" : "0px",
                      position: "relative",
                    }}
                  >
                    {currentBaseImageUrl ? (
                      <img
                        src={tosThumbnail(currentBaseImageUrl, 200)}
                        alt={t("baseImage.title")}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontSize: "0.93rem",
                          fontWeight: 600,
                          textAlign: "center",
                          lineHeight: 1.2,
                          color:
                            baseMetadata.baseImageUrls.length > 0
                              ? "rgba(234, 179, 8, 0.8)"
                              : "var(--text-3)",
                        }}
                      >
                        {baseMetadata.baseImageUrls.length > 0
                          ? t("character.toSelect")
                          : t("character.toGenerate")}
                      </span>
                    )}
                    {baseMetadata.baseImageUrls.length >
                      (seenCounts["base"] || 0) && (
                      <span
                        style={{
                          position: "absolute",
                          top: "2px",
                          left: "2px",
                          background: "#22c55e",
                          color: "#fff",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          padding: "2px 5px",
                          borderRadius: "3px",
                          lineHeight: 1,
                          zIndex: 3,
                        }}
                      >
                        NEW
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveStateKey(baseStateKey);
                        if (characterImageWorkflow === "casting_styling") {
                          setStyleWorkspaceOpen(true);
                        } else {
                          setShowHistoryModal(true);
                        }
                        markThumbSeen(
                          "base",
                          baseMetadata.baseImageUrls.length,
                        );
                      }}
                      style={{
                        position: "absolute",
                        top: "3px",
                        right: "3px",
                        background: "linear-gradient(135deg, #3b82f6, #60a5fa)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        fontSize: "0.84rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        zIndex: 2,
                      }}
                    >
                      {t("baseImage.designPrototype")}
                    </button>
                  </div>
                </div>
                {/* Description — spans remaining columns */}
                <div
                  style={{
                    gridColumn: `2 / -1`,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {activeDescription &&
                    (activeDescription.appearance ||
                      activeDescription.personality) && (
                      <>
                        <div
                          style={{
                            fontSize: "0.93rem",
                            fontWeight: 600,
                            color: "var(--theme-character)",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem",
                            marginBottom: "0.3rem",
                          }}
                        >
                          <span
                            style={{
                              width: "3px",
                              height: "1rem",
                              background: "var(--theme-character)",
                              borderRadius: "2px",
                              flexShrink: 0,
                            }}
                          />
                          {t("sectionTitle.description")}
                        </div>
                        <div
                          style={{
                            fontSize: "0.8rem",
                            color: "var(--text-2)",
                            lineHeight: 1.5,
                            padding: "0.5rem 0.6rem",
                            borderRadius: "6px",
                            background:
                              "color-mix(in srgb, var(--theme-character) 5%, transparent)",
                          }}
                        >
                          {activeDescription.appearance && (
                            <p style={{ margin: "0 0 0.2rem" }}>
                              {t("character.appearance")}
                              {activeDescription.appearance}
                            </p>
                          )}
                          {activeDescription.personality && (
                            <p style={{ margin: "0 0 0.2rem" }}>
                              {t("character.personality")}
                              {activeDescription.personality}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                </div>
              </div>
              {/* Section: State Images */}
              <div
                style={{
                  fontSize: "0.93rem",
                  fontWeight: 600,
                  color: "var(--theme-character)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.35rem",
                  marginBottom: "0.25rem",
                }}
              >
                <span
                  style={{
                    width: "3px",
                    height: "1rem",
                    background: "var(--theme-character)",
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                />
                {t("sectionTitle.stateImages")}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${thumbColumns}, 1fr)`,
                  gap: "0.4rem",
                }}
              >
                {/* State thumbnails */}
                {states.map((s, i) => {
                  const label = s.state || t("character.defaultState");
                  const taskKey = activeTaskKey(
                    "character",
                    s.state ? `${s.name}|${s.state}` : s.name,
                  );
                  const inFlight = activeTaskMap.get(taskKey) ?? [];
                  const isGenerating = inFlight.length > 0;
                  const slotFailures = failedTaskMap?.get(taskKey) ?? [];
                  const isFailed = slotFailures.length > 0;
                  return (
                    <div
                      key={i}
                      onClick={() => setActiveStateKey(stateKey(s.state))}
                      style={{
                        aspectRatio: "1",
                        borderRadius: "6px",
                        overflow: "hidden",
                        cursor: "pointer",
                        background: s.selectedImageUrl
                          ? "transparent"
                          : isGenerating
                            ? "rgba(34, 197, 94, 0.12)"
                            : isFailed
                              ? "rgba(239, 68, 68, 0.10)"
                              : (s.imageUrls?.length || 0) > 0
                                ? "rgba(234, 179, 8, 0.12)"
                                : "rgba(156, 163, 175, 0.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        outline:
                          activeIdx === i
                            ? "3px solid var(--theme-scene)"
                            : "1px solid var(--border)",
                        outlineOffset: activeIdx === i ? "1px" : "0px",
                        position: "relative",
                      }}
                    >
                      {s.selectedImageUrl ? (
                        <>
                          <img
                            src={tosThumbnail(s.selectedImageUrl, 200)}
                            alt={label}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                          {isGenerating && (
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(34, 197, 94, 0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                              }}
                            >
                              <span
                                style={{
                                  color: "#fff",
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  textShadow: "0 1px 3px rgba(0,0,0,0.4)",
                                }}
                              >
                                {t("generating", { ns: "common" })}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.93rem",
                            fontWeight: 600,
                            textAlign: "center",
                            lineHeight: 1.2,
                            color: isGenerating
                              ? "rgba(34, 197, 94, 0.8)"
                              : (s.imageUrls?.length || 0) > 0
                                ? "rgba(234, 179, 8, 0.8)"
                                : "var(--text-3)",
                          }}
                        >
                          {isGenerating
                            ? t("generating", { ns: "common" })
                            : (s.imageUrls?.length || 0) > 0
                              ? t("character.toSelect")
                              : t("character.toGenerate")}
                        </span>
                      )}
                      {isFailed && (
                        <span
                          style={{
                            position: "absolute",
                            top: "2px",
                            left: "2px",
                            background: "var(--danger)",
                            color: "#fff",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            padding: "2px 5px",
                            borderRadius: "3px",
                            lineHeight: 1,
                            zIndex: 3,
                          }}
                        >
                          {t("character.failedBadge")}
                        </span>
                      )}
                      {!isFailed &&
                        (s.imageUrls?.length || 0) >
                          (seenCounts[s.state || "default"] || 0) && (
                          <span
                            style={{
                              position: "absolute",
                              top: "2px",
                              left: "2px",
                              background: "#22c55e",
                              color: "#fff",
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              padding: "2px 5px",
                              borderRadius: "3px",
                              lineHeight: 1,
                              zIndex: 3,
                            }}
                          >
                            NEW
                          </span>
                        )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveStateKey(stateKey(s.state));
                          setShowHistoryModal(true);
                          markThumbSeen(
                            s.state || "default",
                            s.imageUrls?.length || 0,
                          );
                        }}
                        style={{
                          position: "absolute",
                          top: "3px",
                          right: "3px",
                          background:
                            "linear-gradient(135deg, #3b82f6, #60a5fa)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "2px 6px",
                          fontSize: "0.84rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                      >
                        {t("baseImage.produce")}
                      </button>
                      <div
                        style={{
                          position: "absolute",
                          bottom: "2px",
                          left: "2px",
                          right: "2px",
                          background: "rgba(0,0,0,0.4)",
                          color: "white",
                          fontSize: "0.75rem",
                          textAlign: "center",
                          borderRadius: "2px",
                          padding: "1px 2px",
                          lineHeight: 1.25,
                          wordBreak: "break-all",
                          height: "calc(0.75rem * 1.25 * 2 + 2px)",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {/* Add state button in grid */}
                <div
                  onClick={() => {
                    if (!hasBaseMetadataConflict) setAddingState(true);
                  }}
                  aria-disabled={hasBaseMetadataConflict}
                  title={hasBaseMetadataConflict ? t("baseImage.conflict.addStateBlocked") : undefined}
                  style={{
                    aspectRatio: "1",
                    borderRadius: "6px",
                    cursor: hasBaseMetadataConflict ? "not-allowed" : "pointer",
                    opacity: hasBaseMetadataConflict ? 0.55 : 1,
                    border: "1px dashed var(--border)",
                    background: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "2px",
                    color: "var(--text-3)",
                  }}
                >
                  <span style={{ fontSize: "1.4rem", lineHeight: 1 }}>+</span>
                  <span style={{ fontSize: "0.93rem", lineHeight: 1 }}>
                    {hasBaseMetadataConflict
                      ? t("baseImage.conflict.addStateBlocked")
                      : t("character.newState")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {previewUrl &&
            ReactDOM.createPortal(
              <Lightbox
                urls={[previewUrl]}
                index={0}
                onClose={() => setPreviewUrl(null)}
                onChange={() => {}}
              />,
              document.body,
            )}

          {/* Add state modal */}
          {addingState &&
            ReactDOM.createPortal(
              <div className="modal-overlay">
                <div
                  className="modal-content"
                  style={{ width: "400px", maxWidth: "90vw" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "1rem",
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: "1rem" }}>
                      {name} - {t("character.newState")}
                    </h3>
                    <button
                      onClick={cancelAdd}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "1.2rem",
                        cursor: "pointer",
                        color: "var(--text-3)",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      type="text"
                      value={newStateName}
                      onChange={(e) => setNewStateName(e.target.value)}
                      placeholder={t("character.stateNamePlaceholder")}
                      style={{
                        fontSize: "0.85rem",
                        border: "1px solid var(--primary)",
                        borderRadius: "4px",
                        padding: "6px 10px",
                      }}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Escape") cancelAdd();
                        if (e.key === "Enter" && canAddState)
                          handleConfirmAddState();
                      }}
                    />
                    <textarea
                      value={newStateDesc}
                      onChange={(e) => setNewStateDesc(e.target.value)}
                      placeholder={t("character.stateDescPlaceholder")}
                      rows={3}
                      style={{
                        fontSize: "0.85rem",
                        border: "1px solid var(--border)",
                        borderRadius: "4px",
                        padding: "6px 10px",
                        resize: "vertical",
                        fontFamily: "inherit",
                      }}
                    />
                    <div style={{ fontSize: "0.8rem", color: "var(--text-2)", marginTop: "0.25rem" }}>
                      {t("addElement.stateEpisodeLabel")}
                    </div>
                    <EpisodeMultiSelect
                      episodes={availableEpisodes ?? []}
                      selected={newStateEpisodes}
                      onChange={setNewStateEpisodes}
                    />
                    {hasBaseMetadataConflict && (
                      <div style={{ fontSize: "0.78rem", color: "var(--danger, #d33)" }}>
                        {t("baseImage.conflict.addStateBlocked")}
                      </div>
                    )}
                    {newStateEpisodes.length === 0 && (
                      <div style={{ fontSize: "0.78rem", color: "var(--danger, #d33)", marginTop: "0.15rem" }}>
                        {t("addElement.stateEpisodeRequiredHint")}
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        justifyContent: "flex-end",
                        marginTop: "0.25rem",
                      }}
                    >
                      <button
                        onClick={cancelAdd}
                        style={{
                          background: "none",
                          border: "1px solid var(--border)",
                          color: "var(--text-2)",
                          borderRadius: "4px",
                          padding: "6px 16px",
                          fontSize: "0.85rem",
                          cursor: "pointer",
                        }}
                      >
                        {t("cancel", { ns: "common" })}
                      </button>
                      <button
                        onClick={handleConfirmAddState}
                        disabled={!canAddState}
                        style={{
                          background: "var(--primary)",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "6px 16px",
                          fontSize: "0.85rem",
                          cursor: "pointer",
                        }}
                      >
                        {addingLoading
                          ? t("adding", { ns: "common" })
                          : t("confirm", { ns: "common" })}
                      </button>
                    </div>
                  </div>
                </div>
              </div>,
              document.body,
            )}

          {/* History Modal */}
          {showHistoryModal &&
            ReactDOM.createPortal(
              <div className="modal-overlay">
                <div
                  className="modal-content"
                  style={
                    hasGenInfo
                      ? {
                          width: "90vw",
                          maxWidth: "90vw",
                          height: "90vh",
                          maxHeight: "90vh",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }
                      : {
                          width: "80vw",
                          maxWidth: "80vw",
                          height: "80vh",
                          maxHeight: "80vh",
                          overflow: "auto",
                        }
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "1rem",
                      flexShrink: 0,
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: "1rem" }}>
                      {name}
                      {activeIdx === -1
                        ? ` - ${t("baseImage.title")}`
                        : states[safeIdx]?.state
                          ? ` - ${states[safeIdx].state}`
                          : ""}
                    </h3>
                    <button
                      onClick={() => setShowHistoryModal(false)}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "1.2rem",
                        cursor: "pointer",
                        color: "var(--text-3)",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    style={
                      hasGenInfo
                        ? {
                            display: "flex",
                            gap: "1rem",
                            flex: 1,
                            minHeight: 0,
                          }
                        : undefined
                    }
                  >
                    <div
                      style={
                        hasGenInfo
                          ? { flex: 1, minWidth: 0, overflowY: "auto" }
                          : undefined
                      }
                    >
                      {activeIdx === -1 &&
                      characterImageWorkflow === "casting_styling" ? (
                        <CharacterStyleWorkspace
                          open={showHistoryModal}
                          projectId={projectId}
                          characterName={name}
                          charFixedPrompt={fixedPrompt}
                          projectRefImages={projectRefImages}
                          platform={imagePlatform}
                          model={imageModel}
                          baseMetadata={baseMetadata}
                          analysisAuthorityRevision={analysisAuthorityRevision}
                          onBaseMetadataChanged={onBaseGenerated}
                          onClose={() => {
                            setShowHistoryModal(false);
                            onBaseGenerated?.();
                          }}
                        />
                      ) : activeIdx === -1 ? (
                        <BaseImageBlock
                          projectId={projectId}
                          elementType="character"
                          name={name}
                          fixedPrompt={fixedPrompt}
                          mostEpisodesPrompt={mostEp?.imagePrompt || ""}
                          initBaseRefImages={baseMetadata.baseRefImages}
                          initBaseImageUrls={baseMetadata.baseImageUrls}
                          initSelectedBaseImageUrl={selectedBaseImageUrl || undefined}
                          conflictFields={effectiveBaseConflictFields}
                          analysisAuthorityRevision={analysisAuthorityRevision}
                          onSelectBaseImage={(url) =>
                            setCurrentBaseImageUrl(url || undefined)
                          }
                          onBaseGenerated={onBaseGenerated}
                          activeBaseTasks={inFlightBase}
                          projectRefImages={projectRefImages}
                          failedBaseTasks={baseFailures}
                          onSearchSelect={
                            onSearchSelectAllStates
                              ? (count: number) =>
                                  onSearchSelectAllStates(
                                    "character",
                                    name,
                                    count,
                                  )
                              : undefined
                          }
                          searchImageCount={
                            getSearchImageCountAllStates
                              ? getSearchImageCountAllStates("character", name)
                              : 0
                          }
                          searchTaskProgress={
                            getSearchTaskProgressAllStates
                              ? getSearchTaskProgressAllStates(
                                  "character",
                                  name,
                                )
                              : undefined
                          }
                        />
                      ) : (
                        <ElementCard
                          key={elementName}
                          projectId={projectId}
                          elementType="character"
                          name={elementName}
                          imagePrompt={c.imagePrompt}
                          promptIncludesPrefix={c.promptIncludesPrefix}
                          imageUrls={c.imageUrls}
                          refImageUrls={c.refImageUrls}
                          selectedImageUrl={c.selectedImageUrl}
                          selectedBaseImageUrl={currentBaseImageUrl}
                          activeTasks={
                            activeTaskMap.get(
                              activeTaskKey("character", elementName),
                            ) ?? []
                          }
                          failedTasks={
                            failedTaskMap?.get(
                              activeTaskKey("character", elementName),
                            ) ?? []
                          }
                          onTaskResolved={onTaskResolved}
                          episodes={c.episodes}
                          episodeMap={episodeMap}
                          statePromptPrefix={charStatePromptPrefix}
                          imageModels={imageModels}
                          defaultImagePlatform={imagePlatform}
                          defaultImageModel={imageModel}
                          onUpdate={onUpdate}
                          onSearchSelect={
                            onSearchSelect
                              ? () =>
                                  onSearchSelect(
                                    "character",
                                    c.name,
                                    c.state || "",
                                    (c.refImageUrls || []).length,
                                  )
                              : undefined
                          }
                          searchImageCount={
                            getSearchImageCount
                              ? getSearchImageCount(
                                  "character",
                                  c.name,
                                  c.state || "",
                                )
                              : 0
                          }
                          searchTaskProgress={
                            getSearchTaskProgress
                              ? getSearchTaskProgress(
                                  "character",
                                  c.name,
                                  c.state || "",
                                )
                              : undefined
                          }
                          reviewedImageStatus={reviewStatusMap?.get(
                            `${c.name}|${c.state || ""}`,
                          )}
                          onReviewInvalidated={onReviewInvalidated}
                          availableEpisodes={availableEpisodes}
                          onEpisodesUpdate={
                            onEpisodesUpdate
                              ? (next) =>
                                  onEpisodesUpdate(c.name, c.state || "", next)
                              : undefined
                          }
                          characterImageWorkflow={characterImageWorkflow}
                          characterName={name}
                          elementState={c.state || ""}
                          onRefImagesUpdated={() =>
                            onUpdate?.(
                              "character",
                              elementName,
                              c.imagePrompt || "",
                              c.imageUrls,
                              c.refImageUrls,
                              c.selectedImageUrl,
                            )
                          }
                          getGenerateWarnings={() => {
                            const warnings: string[] = [];
                            if (!currentBaseImageUrl) {
                              warnings.push(t("character.noBaseImageWarning"));
                            }
                            return warnings;
                          }}
                          appearance={c.appearance}
                          personality={c.personality}
                          onGenerateComplete={onGenerateComplete}
                        >
                          <div style={{ display: "none" }}>
                            {c.appearance && <p>{c.appearance}</p>}
                            {c.personality && <p>{c.personality}</p>}
                            {c.look && c.look.length > 0 && (
                              <div>
                                {c.look.map((propName, idx) => {
                                  const propsWithImage = allProps.filter(
                                    (p) =>
                                      p.name === propName && p.selectedImageUrl,
                                  );
                                  const hasImage = propsWithImage.length > 0;
                                  const isImporting =
                                    importingProp === propName;
                                  const isPickerOpen =
                                    propStatePicker?.propName === propName;
                                  return (
                                    <span
                                      key={propName}
                                      style={{
                                        whiteSpace: "nowrap",
                                        position: "relative",
                                      }}
                                    >
                                      {idx > 0 && ", "}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          onNavigateToProp?.(propName);
                                        }}
                                        style={{
                                          color: "var(--primary)",
                                          background: "none",
                                          border: "none",
                                          padding: 0,
                                          font: "inherit",
                                          textDecoration: "none",
                                          cursor: "pointer",
                                        }}
                                      >
                                        {propName}
                                      </button>
                                      <Tooltip
                                        text={
                                          hasImage
                                            ? t("character.importPropRef", {
                                                name: propName,
                                              })
                                            : t("character.generatePropFirst")
                                        }
                                      >
                                        <button
                                          onClick={() =>
                                            hasImage &&
                                            handleImportPropRef(propName)
                                          }
                                          disabled={!hasImage || isImporting}
                                          style={{
                                            fontSize: "0.7rem",
                                            padding: "0px 4px",
                                            marginLeft: "3px",
                                            border: "1px solid",
                                            borderRadius: "3px",
                                            cursor: hasImage
                                              ? "pointer"
                                              : "not-allowed",
                                            background: hasImage
                                              ? "var(--primary-light)"
                                              : "var(--surface-2)",
                                            color: hasImage
                                              ? "var(--primary)"
                                              : "var(--text-3)",
                                            borderColor: hasImage
                                              ? "#c7d2fe"
                                              : "var(--border)",
                                            opacity: isImporting ? 0.6 : 1,
                                            verticalAlign: "middle",
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          {isImporting
                                            ? "..."
                                            : t("character.importBtn")}
                                        </button>
                                      </Tooltip>
                                      {isPickerOpen && (
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "100%",
                                            left: 0,
                                            zIndex: 100,
                                            marginTop: "4px",
                                            background: "var(--surface)",
                                            border: "1px solid var(--border)",
                                            borderRadius: "6px",
                                            boxShadow:
                                              "0 4px 12px rgba(0,0,0,0.15)",
                                            padding: "6px 0",
                                            minWidth: "160px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              padding: "4px 10px",
                                              fontSize: "0.75rem",
                                              color: "var(--text-3)",
                                              borderBottom:
                                                "1px solid var(--border)",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            {t("character.selectPropState")}
                                          </div>
                                          {propStatePicker.options.map(
                                            (opt) => (
                                              <div
                                                key={opt.state || "__default"}
                                                onClick={() =>
                                                  doImportPropRef(opt)
                                                }
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                  padding: "6px 10px",
                                                  cursor: "pointer",
                                                  fontSize: "0.8rem",
                                                  color: "var(--text-1)",
                                                }}
                                              >
                                                <img
                                                  src={
                                                    opt.selectedImageUrl
                                                      ? tosThumbnail(
                                                          opt.selectedImageUrl,
                                                          32,
                                                        )
                                                      : ""
                                                  }
                                                  alt=""
                                                  style={{
                                                    width: "32px",
                                                    height: "32px",
                                                    objectFit: "cover",
                                                    borderRadius: "4px",
                                                    flexShrink: 0,
                                                  }}
                                                />
                                                <span>
                                                  {opt.state ||
                                                    t(
                                                      "character.defaultStateName",
                                                    )}
                                                </span>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {c.containLines && <p>{c.containLines}</p>}
                          </div>
                        </ElementCard>
                      )}
                    </div>
                    {hasGenInfo && registry && (
                      <AssetGenInfoPanel
                        registry={registry}
                        assetType="character"
                        assetName={name}
                        episodes={activeIdx === -1 ? undefined : c?.episodes}
                      />
                    )}
                  </div>
                </div>
              </div>,
              document.body,
            )}
        </>
      )}
    </div>
  );
};
